import React from 'react';
import { parseISO } from 'date-fns';
import { formatDate } from '../../../helpers/dates';
import { getWeekMeta } from '../../../helpers/desk-booking';
import type { DeskDayState } from '../../../helpers/desk-booking';

// A desk's day state, or one of the extra states of "My days" and block mode.
export type WeekGridCellState = DeskDayState | 'booked' | 'empty' | 'picking' | 'blockable';

export type WeekGridCell = {
  state: WeekGridCellState,
  label: string,
  disabled: boolean,
  onClick?: () => void,
};

type Props = {
  weeks: Array<Array<string>>,
  today: string,
  mapDate?: string,
  busy: boolean,
  renderCell: (date: string) => WeekGridCell,
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// 4-week Mon–Fri grid with a week column, shared by "My days" and the desk calendar.
const WeekGrid = (props: Props): React.ReactElement => {
  const { weeks, today, mapDate, busy, renderCell } = props;

  return (
    <table className="week-grid">
      <thead>
        <tr>
          <th className="week-grid__week-head">Week</th>
          {WEEKDAYS.map((day) => <th key={day}>{day}</th>)}
        </tr>
      </thead>
      <tbody>
        {weeks.map((week) => {
          const meta = getWeekMeta(week, today);

          return (
            <tr key={week[0]} className={meta.isCurrent ? 'week-grid__row--current' : ''}>
              <th scope="row" className="week-grid__week">
                <strong>{meta.title}</strong>
                <small>{meta.dates}</small>
                <small className={meta.isCurrent ? 'week-grid__relative--current' : ''}>{meta.relative}</small>
              </th>
              {week.map((date) => {
                const cell = renderCell(date);
                const isToday = date === today;
                const onMap = date === mapDate;
                let classes = `week-grid__cell week-grid__cell--${cell.state}`;
                if (isToday) classes += ' week-grid__cell--today';
                if (onMap) classes += ' week-grid__cell--on-map';

                return (
                  <td key={date}>
                    <button
                      type="button"
                      className={classes}
                      disabled={busy || cell.disabled}
                      title={cell.label}
                      onClick={cell.onClick}
                    >
                      {isToday && <span className="week-grid__badge week-grid__badge--top">Today</span>}
                      <span className="week-grid__date">{formatDate(parseISO(date), 'dd.MM.')}</span>
                      <span className="week-grid__label">{cell.label}</span>
                      {onMap && <span className="week-grid__badge week-grid__badge--bottom">On map</span>}
                    </button>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default WeekGrid;
